import { useState, useEffect, useCallback } from "react";
import { Zap, ArrowRight, Clock, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { type BoostedLoad } from "@/hooks/useBoostedLoads";
import { DemoHint } from "@/components/demo/DemoHint";

interface FeaturedLoadsRailProps {
  boostedLoads: BoostedLoad[];
  onSelectLoad: (loadId: string) => void;
}

const STORAGE_KEY = "lr-featured-rail-collapsed";

function timeRemaining(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Expiring";
  const mins = Math.ceil(ms / 60000);
  return `${mins}m left`;
}

const eqMap: Record<string, string> = {
  van: "Van", reefer: "Reefer", flatbed: "Flatbed",
  step_deck: "Step Deck", power_only: "PO", box_truck: "Box",
  hotshot: "Hotshot", other: "Other",
};

export function FeaturedLoadsRail({ boostedLoads, onSelectLoad }: FeaturedLoadsRailProps) {
  const [, setTick] = useState(0);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const toggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  // Tick every 30s to update countdown
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  if (boostedLoads.length === 0) return null;

  return (
    <div className="border-b border-border bg-gradient-to-r from-brand-teal/5 to-transparent">
      {/* Header — always visible */}
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-brand-teal/5 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <Zap className="h-3 w-3 text-brand-teal" />
          <DemoHint hintKey="section.featuredLoads" side="right" showIndicator>
          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-teal">
            Featured Loads
          </span>
          </DemoHint>
          <span className="text-[10px] text-muted-foreground">
            ({boostedLoads.length})
          </span>
        </div>
        {collapsed ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>

      {/* Cards — collapsible */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          collapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="flex gap-2 overflow-x-auto px-3 pb-2">
            {boostedLoads.map((load) => (
              <button
                key={load.boost_id}
                onClick={() => onSelectLoad(load.id)}
                className={cn(
                  "flex-shrink-0 w-[220px] rounded-lg border border-brand-teal/30 bg-card",
                  "hover:border-brand-teal/60 hover:shadow-md transition-all duration-150",
                  "text-left p-2.5 group cursor-pointer"
                )}
                style={{ boxShadow: "0 1px 4px hsl(var(--brand-teal) / 0.08)" }}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-1.5">
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-brand-teal bg-brand-teal/10 rounded-full px-1.5 py-0.5">
                    <Zap className="h-2.5 w-2.5" />
                    Boosted
                  </span>
                  <span className="inline-flex items-center gap-0.5 text-[9px] text-muted-foreground">
                    <Clock className="h-2.5 w-2.5" />
                    {timeRemaining(load.boost_expires_at)}
                  </span>
                </div>

                {/* Route */}
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs font-semibold text-foreground truncate">
                    {load.origin_city}, {load.origin_state}
                  </span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="text-xs font-semibold text-foreground truncate">
                    {load.destination_city}, {load.destination_state}
                  </span>
                </div>

                {/* Details row */}
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  {load.rate_amount > 0 && (
                    <span className="font-bold text-foreground">
                      ${load.rate_amount.toLocaleString()}
                    </span>
                  )}
                  {load.total_miles > 0 && (
                    <span>{load.total_miles.toLocaleString()} mi</span>
                  )}
                  <span className="bg-muted rounded px-1 py-0.5 text-[9px] font-medium">
                    {eqMap[load.equipment_type] || load.equipment_type}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
